import React from 'react';
import { Link } from 'react-router-dom';
import { MessageEntity, UserCountersEntity, UserEntity } from '@interpals/api-web';
import { MiddlewareAPI } from '@reduxjs/toolkit';

import ROUTES, { makeAlbumPhotoURL, makeAlbumURL, makeSinglePostURL, makeThreadURL, makeProfileURL } from 'routes';
import { updateCounters } from 'redux/counters/actions';
import { setNotificationsLastFetched } from 'redux/notifications/actions';
import { createCustomToast } from 'services/toasts';
import i18n from 'utils/i18n';
import { Dispatch, RootState } from '../index';
import { markThreadViewed, setLastThreadMessage } from '../../chat/actions';

// ws: thread typing
const threadTypingTimers: { [threadId: string]: number } = {};

const handleThreadTyping = (store: MiddlewareAPI, userId: string): void => {
    const currentTimer = threadTypingTimers[userId];
    if (currentTimer) {
        clearTimeout(currentTimer);
    } else {
        store.dispatch({ type: 'ws/set-thread-typing', payload: userId });
    }

    threadTypingTimers[userId] = window.setTimeout(() => {
        store.dispatch({ type: 'ws/remove-thread-typing', payload: userId });

        delete threadTypingTimers[userId];
    }, 1500);
};

// ws: new message
const handleNewMessage = (
    store: MiddlewareAPI<Dispatch, RootState>,
    wsMessage: { data: MessageEntity; sender: UserEntity; counters: UserCountersEntity },
): void => {
    if (window.location.pathname !== makeThreadURL(wsMessage.sender.id)) {
        // Todo: Figure out how to call navigate() on router outside of
        // component context and pass an onClick in the options below instead
        // of using these <Link /> components that don't cover the entire
        // toast area.
        createCustomToast(
            'info',
            <Link to={makeThreadURL(wsMessage.sender.id)}>{i18n.t('Toast.NewMessage')}</Link>,
            <Link to={makeThreadURL(wsMessage.sender.id)}>{wsMessage.sender.name}</Link>,
            wsMessage.sender.avatar.thumb_small,
        );

        store.dispatch({ type: 'ws/messages-new-message', payload: wsMessage.data });
    }

    if (window.location.pathname === makeThreadURL(wsMessage.sender.id)) {
        store.dispatch({ type: 'ws/messages-new-message', payload: wsMessage.data });
        store.dispatch(markThreadViewed({ id: wsMessage.data.thread_id, message_id: wsMessage.data.id }));
        store.dispatch(setLastThreadMessage(wsMessage.data));
    }

    store.dispatch(updateCounters(wsMessage.counters));
};

// ws: sync messages
const handleSyncMessage = (store: MiddlewareAPI, wsMessage: { data: MessageEntity; contact: UserEntity }): void => {
    if (window.location.pathname === makeThreadURL(wsMessage.contact.id)) {
        store.dispatch({ type: 'WS_SYNC_MESSAGE', payload: { message: wsMessage.data } });
    }
    if (window.location.pathname.startsWith('/messages')) {
        store.dispatch({ type: 'WS_THREAD_SYNC_MESSAGE', payload: wsMessage });
    }
};

// ws: rest events
export const onMessage = (event: MessageEvent, store: MiddlewareAPI): void => {
    const data = event.data;

    if (!data) return;

    const payload = JSON.parse(String(data));

    if (process.env.NODE_ENV !== 'production') {
        store.dispatch({ type: 'WS_MESSAGE', payload });
    }

    switch (payload.type) {
        case 'THREAD_TYPING':
            handleThreadTyping(store, payload.sender.id);
            break;
        case 'THREAD_NEW_MESSAGE':
            handleNewMessage(store, payload);
            break;
        case 'THREAD_VIEWED':
            store.dispatch({ type: 'ws/set-last-viewed-message', payload });
            break;
        case 'THREAD_SYNC_MESSAGE':
            handleSyncMessage(store, payload);
            break;
        case 'PROFILE_VIEW': {
            store.dispatch(updateCounters(payload.counters));
            createCustomToast(
                'info',
                `${i18n.t('Toast.ProfileView')}`,
                <Link to={makeProfileURL(payload.sender.username)}>{payload.sender.name}</Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'FRIEND_REQUEST_MESSAGE': {
            store.dispatch(setNotificationsLastFetched(undefined));
            store.dispatch(updateCounters(payload.counters));

            createCustomToast(
                'info',
                `${i18n.t('Toast.FriendRequest')}`,
                <Link to={ROUTES.FRIENDS_REQUEST}>{payload.sender.name}</Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'LIKE_PHOTO': {
            store.dispatch(updateCounters(payload.counters));

            createCustomToast(
                'info',
                `${i18n.t('Toast.LikePhoto')}`,
                <Link
                    to={makeAlbumPhotoURL({
                        username: payload.sender.username,
                        uid: payload.sender.id,
                        photoId: payload.data.id,
                        albumId: payload.data.aid,
                    })}
                >
                    {payload.sender.name}
                </Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'LIKE_POST': {
            store.dispatch(updateCounters(payload.counters));

            createCustomToast(
                'info',
                `${i18n.t('Toast.LikePost')}`,
                <Link to={makeSinglePostURL(payload.data.poster_uid, payload.data.id)}>{payload.sender.name}</Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'LIKE_COMMENT': {
            store.dispatch(updateCounters(payload.counters));

            createCustomToast(
                'info',
                `${i18n.t('Toast.LikeComment')}`,
                <Link to={makeSinglePostURL(payload.data.poster_uid, payload.data.id)}>{payload.sender.name}</Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'LIKE_ALBUM': {
            store.dispatch(updateCounters(payload.counters));

            const { username, uid } = store.getState().account;

            if (!username || !uid) {
                throw new Error('Notification error: Account not set');
            }

            createCustomToast(
                'info',
                `${i18n.t('Toast.LikeAlbum')}`,
                <Link to={makeAlbumURL(username, uid, payload.data.id)}>{payload.sender.name}</Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'LIKE_TRIP': {
            store.dispatch(updateCounters(payload.counters));

            createCustomToast(
                'info',
                `${i18n.t('Toast.LikeTrip')}`,
                <Link to={makeProfileURL(payload.sender.username)}>{payload.sender.name}</Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'LIKE_GROUP_POST': {
            store.dispatch(updateCounters(payload.counters));

            createCustomToast(
                'info',
                `${i18n.t('Toast.LikePost')}`,
                <Link to={makeProfileURL(payload.sender.username)}>{payload.sender.name}</Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'COMMENT_PHOTO': {
            store.dispatch(updateCounters(payload.counters));

            createCustomToast(
                'info',
                `${i18n.t('Toast.CommentPhoto')}`,
                <Link
                    to={makeAlbumPhotoURL({
                        username: payload.sender.username,
                        uid: payload.data.uid,
                        photoId: payload.data.id,
                        albumId: payload.data.aid,
                    })}
                    state={{ branch: true }}
                >
                    {payload.sender.name}
                </Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'COMMENT_ALBUM': {
            store.dispatch(updateCounters(payload.counters));

            const { username, uid } = store.getState().account;

            if (!username || !uid) {
                throw new Error('Notification error: Account not set');
            }

            createCustomToast(
                'info',
                `${i18n.t('Toast.CommentAlbum')}`,
                <Link to={makeAlbumURL(username, uid, payload.data.id)}>{payload.sender.name}</Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'COMMENT_POST': {
            store.dispatch(updateCounters(payload.counters));

            createCustomToast(
                'info',
                `${i18n.t('Toast.CommentPost')}`,
                <Link to={makeSinglePostURL(payload.data.poster_uid, payload.data.id)}>{payload.sender.name}</Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'COMMENT_TRIP': {
            store.dispatch(updateCounters(payload.counters));

            createCustomToast(
                'info',
                `${i18n.t('Toast.CommentTrip')}`,
                <Link to={makeProfileURL(payload.sender.username)}>{payload.sender.name}</Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'FRIEND_BIRTHDAY': {
            store.dispatch(updateCounters(payload.counters));

            createCustomToast(
                'info',
                `${i18n.t('Toast.FriendBirthday', {
                    name: payload.sender.name,
                })}`,
                <Link to={ROUTES.FRIENDS_BIRTHDAYS}>{payload.sender.name}</Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'FRIEND_ACCEPT_MESSAGE': {
            store.dispatch(updateCounters(payload.counters));

            // Todo: allow ellipsis for part of message in <ToastContent />
            const name =
                String(payload.sender.name).length > 12
                    ? String(payload.sender.name).slice(0, 12) + '...'
                    : String(payload.sender.name);

            createCustomToast(
                'info',
                `${i18n.t('Toast.NewFriend', { name })}`,
                <Link to={makeProfileURL(payload.sender.username)}>{payload.sender.name}</Link>,
                payload.sender.avatar.thumb_small,
            );
            break;
        }
        case 'LIKE_ENTRY':
            // TODO: Add a toast with a link.
            break;
    }
};
